import { useGetTheme } from '@pba/react-theme';
import { Dropdown as DropdownA } from 'antd';
import cn from 'classnames';
import { Children, cloneElement } from 'react';
import type { Ref } from 'react';
import type { DropdownProperties } from '../Dropdown';
import styles from '../dropdown.module.css';
import { isReactElement } from '../utils';
import { usePositionAtClick } from './hooks';

type PositionAtClickDropdownProperties = Omit<DropdownProperties, 'placement' | 'positionAtClick'>;

const PositionAtClickDropdown = ({
  children,
  getPopupContainer,
  menu,
  onOpenChange,
  open: isOpenProperty,
  trigger,
  ...rest
}: PositionAtClickDropdownProperties) => {
  const {
    reactComponents: {
      dropdown: {
        menu: { positionAtClickMinWidth },
      },
    },
  } = useGetTheme();
  const {
    handleOpenChange,
    isOpen,
    placement: clickPlacement,
    setTriggerRef,
  } = usePositionAtClick({
    menuWidth: positionAtClickMinWidth.value,
    onOpenChange,
    open: isOpenProperty,
  });

  const child = Children.only(children);
  const triggerElement = isReactElement<{ ref?: Ref<HTMLElement> }>(child)
    ? cloneElement(child, { ref: setTriggerRef })
    : child;

  return (
    <DropdownA
      // `alignPoint` isn't in antd's public prop types (antd derives it internally
      // from trigger === 'contextMenu'), but it's still forwarded to the underlying
      // rc-trigger at runtime, so this spread is how we force click-point alignment
      // for a non-contextMenu trigger.
      {...{ alignPoint: true }}
      {...rest}
      autoAdjustOverflow={false}
      getPopupContainer={getPopupContainer ?? (() => document.body)}
      menu={
        menu
          ? {
              ...menu,
              className: cn(styles['position-at-click'], menu.className),
            }
          : undefined
      }
      onOpenChange={handleOpenChange}
      open={isOpen}
      placement={clickPlacement}
      trigger={trigger ?? ['click']}
    >
      {triggerElement}
    </DropdownA>
  );
};

export default PositionAtClickDropdown;
